/* ──────────────────────────────────────────────────────────────
   src/api/reports.ts
   Endpoints de generación / consulta de informes
   ────────────────────────────────────────────────────────────── */
import axios from './axios'; // wrapper con baseURL + interceptors

/* ═════════════════════════════════════════════════════════════
   1 ▸ ENCOLAR INFORMES (POST /reports/…)
   ═════════════════════════════════════════════════════════════ */
export async function enqueueHorasProfesor(payload: {
  desde: string;
  hasta: string;
  profesorId?: number;
  centroId?: number;
}) {
  const { data } = await axios.post('/reports/horas-profesor', payload);
  return data as { jobId: string };
}

export async function enqueueHorasProfesorSemanal(payload: { desde: string; hasta: string }) {
  const { data } = await axios.post('/reports/horas-profesor-semanal', payload);
  return data as { jobId: string };
}

export async function enqueueHorasAsignaturaSemanal(payload: { desde: string; hasta: string }) {
  const { data } = await axios.post('/reports/horas-asignatura-semanal', payload);
  return data as { jobId: string };
}

export async function enqueueAsignaturasProfesores() {
  const { data } = await axios.post('/reports/asignaturas-profesores');
  return data as { jobId: string };
}

export async function enqueueAsignaturasDemanda(payload: {
  desde: string;
  hasta: string;
  periodo: 'semanal' | 'mensual';
}) {
  const { data } = await axios.post('/reports/asignaturas-demanda', payload);
  return data as { jobId: string };
}

export async function enqueueAsignaturasAlumnosUnicos(payload: { desde: string; hasta: string }) {
  const { data } = await axios.post('/reports/asignaturas-alumnos-unicos', payload);
  return data as { jobId: string };
}

export async function enqueueAsignaturasRatioProfesor(payload: { desde: string; hasta: string }) {
  const { data } = await axios.post('/reports/asignaturas-ratio-profesor', payload);
  return data as { jobId: string };
}

export async function enqueueAsignaturasCentroFormato(payload: { desde: string; hasta: string }) {
  const { data } = await axios.post('/reports/asignaturas-centro-formato', payload);
  return data as { jobId: string };
}

export async function enqueueHorasProfesoresSemanales() {
  const { data } = await axios.post('/reports/horas-profesores-semanales');
  return data as { jobId: string };
}

export async function enqueueAlumnosProfesor(payload: { desde: string; hasta?: string }) {
  const { data } = await axios.post('/reports/profesor-alumnos', payload);
  return data as { jobId: string };
}

export async function enqueueIncidenciasProfesor(payload: { desde: string; hasta: string }) {
  const { data } = await axios.post('/reports/incidencias-profesor', payload);
  return data as { jobId: string };
}

export async function enqueueRetrasosProfesor(payload: { desde: string; hasta: string }) {
  const { data } = await axios.post('/reports/retrasos-profesor', payload);
  return data as { jobId: string };
}

/* ═════════════════════════════════════════════════════════════
   2 ▸ TIPOS DE FILA (row)
   ═════════════════════════════════════════════════════════════ */
export type HorasCommonBuckets = {
  onsite_wd: number;
  onsite_we: number;
  onsite_hol: number;

  online_wd: number; // ← NUEVO
  online_we: number; // ← NUEVO
  online_hol: number;

  home_wd: number;
  home_we: number;
  home_hol: number;

  cancel_lt2h: number;

  exam_hours: number;
  total_hours: number;
};

/* 2.a  desglose mensual */
export type HorasProfesorRow = { profesorId: number; profesor: string } & HorasCommonBuckets;

/* 2.b  horas semanales */
export type HorasProfesorSemanalRow = {
  profesor: string;
  weekKey: string;
  weekLabel: string;
  horas: number;
};

/* 2.c horas semanales por asignatura */
export type HorasAsignaturaSemanalRow = {
  asignatura: string;
  weekKey: string;
  weekLabel: string;
  horas: number;
};

/* 2.d asignaturas por nº profesores */
export type AsignaturaProfesoresRow = {
  asignaturaId: number;
  asignatura: string;
  numProfesores: number;
};

/* 2.e demanda por asignatura */
export type AsignaturaDemandaRow = {
  asignaturaId: number;
  asignatura: string;
  periodKey: string;
  periodLabel: string;
  numReservas: number;
  horas: number;
};

/* 2.f alumnado único por asignatura */
export type AsignaturaAlumnosUnicosRow = {
  asignaturaId: number;
  asignatura: string;
  alumnosUnicos: number;
  reservasTotales: number;
  recurrenciaPromedio: number;
};

/* 2.g ratio profesor/asignatura */
export type AsignaturaRatioProfesorRow = {
  asignaturaId: number;
  asignatura: string;
  numProfesores: number;
  horasTotales: number;
  cargaMedia: number;
};

/* 2.h comparativa centro/formato */
export type AsignaturaCentroFormatoRow = {
  asignaturaId: number;
  asignatura: string;
  centroId: number;
  centro: string;
  formato: 'Online' | 'Onsite' | 'Home';
  numReservas: number;
  horas: number;
};

/* 2.c horas semanales contratadas por profesor */
export type HorasProfesoresSemanalesRow = {
  profesorId: number;
  profesor: string;
  horasSemanales: number;
};

/* 2.c  alumnos futuros */
export type AlumnosProfesorRow = {
  profesorId: number;
  profesor: string;
  alumnoId: number;
  alumno: string;
  asignaturas: string;
  email: string;
  emailPadre: string | null;
  numClases: number;
  horas: number;
  ultimaReserva: string;
};

/* 2.d  incidencias por profesor */
export type IncidenciasProfesorRow = {
  profesorId: number;
  profesor: string;
  numInc: number;
};

/* ═════════════════════════════════════════════════════════════
   3 ▸ PAYLOADS COMPLETOS (worker → front-end)
   ═════════════════════════════════════════════════════════════ */
export type HorasProfesorResult = {
  url: string;
  registros: number;
  rows: HorasProfesorRow[];
  userId: number;
};
export type HorasProfesorSemanalResult = {
  url: string;
  registros: number;
  rows: HorasProfesorSemanalRow[];
  userId: number;
};
export type HorasAsignaturaSemanalResult = {
  url: string;
  registros: number;
  rows: HorasAsignaturaSemanalRow[];
  userId: number;
};
export type AsignaturaProfesoresResult = {
  url: null;
  registros: number;
  rows: AsignaturaProfesoresRow[];
  userId: number;
};
export type AsignaturaDemandaResult = {
  url: string;
  registros: number;
  rows: AsignaturaDemandaRow[];
  userId: number;
  periodo: 'semanal' | 'mensual';
};
export type AsignaturaAlumnosUnicosResult = {
  url: string;
  registros: number;
  rows: AsignaturaAlumnosUnicosRow[];
  userId: number;
};
export type AsignaturaRatioProfesorResult = {
  url: string;
  registros: number;
  rows: AsignaturaRatioProfesorRow[];
  userId: number;
};
export type AsignaturaCentroFormatoResult = {
  url: string;
  registros: number;
  rows: AsignaturaCentroFormatoRow[];
  userId: number;
};
export type HorasProfesoresSemanalesResult = {
  url: null;
  registros: number;
  rows: HorasProfesoresSemanalesRow[];
  userId: number;
};
export type AlumnosProfesorResult = {
  url: string;
  registros: number;
  rows: AlumnosProfesorRow[];
  userId: number;
};
export type IncidenciasProfesorResult = {
  url: string;
  registros: number;
  rows: IncidenciasProfesorRow[];
  userId: number;
};

/* ===== tipos retrasos profesor ===== */
export type RetrasosProfesorRow = {
  profesorId: number;
  profesor: string;
  numRetrasos: number;
  tiempoTotalMin: number;
  tiempoPromedioMin: number;
  tiempoMaxMin: number;
  tasaRetrasos: number;
  totalReservas: number;
};

export type RetrasosProfesorResult = {
  url: string;
  registros: number;
  rows: RetrasosProfesorRow[];
  userId: number;
};

export type RetrasoReservaRow = {
  id: number;
  fecha: string;
  horaInicio: string;
  horaFin: string;
  retrasoMin: number;
  cliente: string;
  asignatura: string;
  estado: string;
};

export async function getReservasRetrasoProfesor(
  profesorId: number,
  desde: string,
  hasta: string,
): Promise<RetrasoReservaRow[]> {
  const { data } = await axios.get<RetrasoReservaRow[]>(
    `/reports/retrasos-profesor/${profesorId}/reservas`,
    { params: { desde, hasta } },
  );
  return data;
}

/* ===== tipos retrasos alumno ===== */
export type RetrasosAlumnoRow = {
  alumnoId: number;
  alumno: string;
  numRetrasos: number;
  tiempoTotalMin: number;
  tiempoPromedioMin: number;
  tiempoMaxMin: number;
  tasaRetrasos: number;
  totalReservas: number;
};

export type RetrasosAlumnoResult = {
  url: string;
  registros: number;
  rows: RetrasosAlumnoRow[];
  userId: number;
};

/* ===== tipos retrasos reservas ===== */
export type RetrasosReservaGlobalRow = {
  tipo: 'profesor' | 'alumno';
  numRetrasos: number;
  tiempoTotalMin: number;
  tiempoPromedioMin: number;
  tiempoMaxMin: number;
  tasaRetrasos: number;
  totalReservas: number;
};

export type RetrasosReservaDistribRow = {
  id: number;
  nombre: string;
  tipo: 'profesor' | 'alumno';
  numRetrasos: number;
  tiempoTotalMin: number;
  tiempoPromedioMin: number;
  tasaRetrasos: number;
};

export type RetrasosReservaEvolRow = {
  fecha: string;
  numRetrasosProf: number;
  numRetrasosAlu: number;
  tiempoTotalProf: number;
  tiempoTotalAlu: number;
};

export type RetrasosReservasResult = {
  url: string;
  registros: number;
  globales: RetrasosReservaGlobalRow[];
  distribucion: RetrasosReservaDistribRow[];
  evolucion: RetrasosReservaEvolRow[];
  userId: number;
};

/* ===== enqueue informe asignaturas ===== */
export async function enqueueAsignaturasProfesor(payload: { desde: string }) {
  const { data } = await axios.post('/reports/asignaturas-profesor', payload);
  return data as { jobId: string };
}

/* ===== shape filas ===== */
export type AsigProfesorRow = {
  profesorId: number;
  profesor: string;
  asignaturaId: number;
  asignatura: string;
  numReservas: number;
  numAlumnos: number;
  ultimaClase: string; // YYYY-MM-DD
};

export type AsigProfesorResult = {
  url: null;
  registros: number;
  rows: AsigProfesorRow[];
  userId: number;
};

/* ═════════════════════════════════════════════════════════════
   4 ▸ PROGRESO / RESULTADO  (GET /reports/:id/status)
   ═════════════════════════════════════════════════════════════ */
export async function getJobStatus(jobId: string) {
  const { data } = await axios.get(`/reports/${jobId}/status`);
  return data as {
    progress: number;
    done: boolean;
    failed: boolean;
    result:
      | null
      | HorasProfesorResult
      | HorasProfesorSemanalResult
      | HorasAsignaturaSemanalResult
      | HorasProfesoresSemanalesResult
      | AlumnosProfesorResult
      | AsigProfesorResult
      | IncidenciasProfesorResult
      | HorasClienteSemanalResult
      | ProfesoresAlumnoResult
      | IncidenciasAlumnoResult
      | ProgresoAlumnoResult
      | AlumnosProyectoResult
      | TotalAlumnosUnicosResult
      | DistribReservasResult
      | DistribAlumnosResult
      | ReservasDiarioResult
      | AusenciasTotalesResult
      | AgendaProfesorResult
      | AusenciasProfesorResult
      | TrainingTotalResult
      | TrainingPorTipoResult
      | TrainingImpartidasResult
      | TrainingRecibidasResult
      | HpProfesoresResult
      | HpReservasResult
      | HpReservaFirmasResult
      | RetrasosProfesorResult
      | RetrasosAlumnoResult
      | RetrasosReservasResult
      | AsignaturaProfesoresResult
      | AsignaturaDemandaResult
      | AsignaturaAlumnosUnicosResult
      | AsignaturaRatioProfesorResult
      | AsignaturaCentroFormatoResult
      | AlumnosProyectoResult
      | AlumnoProyectoMensualResult;
  };
}

export type BonusA365Row = {
  alumnoId: number;
  alumno: string;
  asignaturaId: number;
  asignatura: string;
  proyecto: string; // ← NUEVO
  horas: number;
  ultimaClase: string;
  checkpoint?: { reservaId: number; fecha: string; horasAcum: number };
};

export type AsigAlumnoRow = {
  alumnoId: number;
  alumno: string;
  ultimaClase: string;
};

export type BonusA365ReservaRow = {
  id: number;
  fecha: string;
  durMin: number; // duración en minutos
  acumMin: number; // horas acumuladas hasta esa clase
  checkpoint: boolean;
};

export type BonusA365Result = {
  registros: number;
  rows: BonusA365Row[];
  userId: number;
};

export type BonusReserva = {
  reservaId: number;
  fecha: string;
  horaInicio: string;
  durClase: number;
  acumHoras: number;
  checkpoint: boolean;
};

export type BonusAlumnoRow = {
  profesorId: number;
  alumnoId: number;
  alumno: string;
  asignaturaId: number;
  asignatura: string;
  reservas: BonusReserva[];
};

export async function enqueueBonusA365(payload: {
  profesorId: number;
  /** rango a analizar */
  desde: string;
  hasta: string;
}) {
  const { data } = await axios.post('/reports/bonus-a365', payload);
  // → { jobId:'…' }
  return data as { jobId: string };
}
/* ─── NUEVO: tipos del informe de ocupación ──────────────────────── */
export type OcupacionDailyRow = {
  fecha: string;
  pct: number;
  horasMax: number;
  horasReales: number;
  horasDisponibles: number;
};
export type OcupacionProfesorRow = {
  profesorId: number;
  profesor: string;
  pct: number;
  horasMax: number;
  horasReales: number;
  horasDisponibles: number;
  semanasCompletas: number;
  semanasSobreOcupacion: number;
  promedioPct: number;
  horasOnline: number;
  horasPresencial: number;
};
export type OcupacionCentroRow = {
  centroId: number | null;
  centro: string;
  pct: number;
  horasMax: number;
  horasReales: number;
  horasDisponibles: number;
};
export type OcupacionTipoRow = {
  tipoId: number | null;
  tipo: string;
  pct: number;
  horasMax: number;
  horasReales: number;
  horasDisponibles: number;
};
export type OcupacionCentroTipoRow = {
  centroId: number | null;
  tipoId: number | null;
  label: string;
  pct: number;
  horasMax: number;
  horasReales: number;
  horasDisponibles: number;
};

export type OcupacionResult = {
  /* payload que te devolvió el worker */
  rowsWeek: OcupacionDailyRow[];
  rowsProfesor: OcupacionProfesorRow[];
  rowsCentro: OcupacionCentroRow[];
  rowsTipo: OcupacionTipoRow[];
  rowsCentroTipo: OcupacionCentroTipoRow[];
  url: string | null; // ⇢ enlace XLSX
  registros: number;
};

/* ─── NUEVO: enqueue ─────────────────────────────────────────────── */
export async function enqueueOcupacionProfesor(payload: {
  desde: string;
  hasta: string;
  profesorId?: number;
  centroId?: number;
  tipoId?: number;
  horaInicio?: string;
  horaFin?: string;
}) {
  const { data } = await axios.post<{ jobId: string }>('/reports/ocupacion-profesor', payload);
  return data; // { jobId }
}

/* ===== enqueue == */
export async function enqueueHorasClienteSemanal(payload: { desde: string; hasta: string }) {
  const { data } = await axios.post('/reports/horas-cliente-semanal', payload);
  return data as { jobId: string };
}

/* ===== tipos fila / resultado == */
export type HorasClienteSemanalRow = {
  cliente: string;
  weekKey: string;
  weekLabel: string;
  horas: number;
};

export type HorasClienteSemanalResult = {
  url: string;
  registros: number;
  rows: HorasClienteSemanalRow[];
  userId: number;
};

export async function enqueueProfesoresAlumno(payload: { desde: string; hasta?: string }) {
  const { data } = await axios.post('/reports/alumno-profesores', payload);
  return data as { jobId: string };
}

/* tipos */
export type ProfesoresAlumnoRow = {
  alumnoId: number;
  alumno: string;
  profesorId: number;
  profesor: string;
  asignaturas: string;
  numClases: number;
  horas: number;
  ultimaReserva: string;
};

export type ProfesoresAlumnoResult = {
  url: string;
  registros: number;
  rows: ProfesoresAlumnoRow[];
  userId: number;
};

export type IncidenciasAlumnoRow = {
  alumnoId: number;
  alumno: string;
  numInc: number;
};
export type IncidenciasAlumnoResult = {
  url: string;
  registros: number;
  rows: IncidenciasAlumnoRow[];
  userId: number;
};
export async function enqueueIncidenciasAlumno(payload: { desde: string; hasta: string }) {
  const { data } = await axios.post('/reports/incidencias-alumno', payload);
  return data as { jobId: string };
}

export async function enqueueRetrasosAlumno(payload: { desde: string; hasta: string }) {
  const { data } = await axios.post('/reports/retrasos-alumno', payload);
  return data as { jobId: string };
}

export async function enqueueRetrasosReservas(payload: { desde: string; hasta: string }) {
  const { data } = await axios.post('/reports/retrasos-reservas', payload);
  return data as { jobId: string };
}

export interface ProgresoAlumnoRow {
  asignatura: string;
  proyecto: string;
  horasTotal: number;
  horasHechas: number;
  ultimaSesion: string; // 'YYYY-MM-DD'  ó  '—'
}
export type ProgresoAlumnoResult = {
  registros: number;
  rows: ProgresoAlumnoRow[];
  url: string; // Excel
};

export async function enqueueProgresoAlumno(payload: { alumnoId: number; desde: string }) {
  const { data } = await axios.post<{ jobId: string }>('/reports/progreso-alumno', payload);
  return data; // { jobId }
}

/** Fila individual que devuelve el worker */
export interface AlumnosProyectoRow {
  proyecto: string; // nombre del proyecto  (— si es NULL)
  numAlu: number; // nº de alumnos únicos en ese proyecto
}

/** Payload completo que devuelve GET /reports/:id/status */
export interface AlumnosProyectoResult {
  url: string;
  registros: number;
  rows: AlumnosProyectoRow[];
  userId: number; // ← añádelo si el worker lo envía
}

/** Encola el job */
export function enqueueAlumnosPorProyecto(p: { desde: string; hasta: string }) {
  return axios.post<{ jobId: string }>('/reports/alumnos-proyecto', p).then((r) => r.data);
}

export interface DistribAlumnosRow {
  label: string;
  numAlu: number;
}

export interface TotalAlumnosUnicosResult {
  url: string;
  registros: number;
  result: {
    totalAlumnos: number;
    porProyecto: DistribAlumnosRow[];
    porGrade: DistribAlumnosRow[];
    porFormato: DistribAlumnosRow[];
    porCentro: DistribAlumnosRow[];
  };
  userId: number;
}

export function enqueueTotalAlumnosUnicos(p: { desde: string; hasta: string }) {
  return axios.post<{ jobId: string }>('/reports/total-alumnos-unicos', p).then((r) => r.data);
}

export function enqueueTotalAlumnosUnicosSegmento(payload: {
  campo: 'proyecto' | 'grade' | 'formato' | 'centro';
  valor: string;
  desde: string;
  hasta: string;
}) {
  return axios
    .post<{ jobId: string }>('/reports/total-alumnos-unicos/alumnos', payload)
    .then((r) => r.data);
}

export type CancelAlumnoRow = {
  alumnoId: number;
  alumno: string;
  reservas: number;
  cancelTot: number;
  cancelAlu: number;
  ratioTot: number;
  ratioAlu: number;
  ultimaCanc: string;
};
export type CancelAlumnoResult = {
  url: string;
  registros: number;
  rows: CancelAlumnoRow[];
  userId: number;
};

export function enqueueCancelacionAlumno(p: { desde: string; hasta: string }) {
  return axios.post<{ jobId: string }>('/reports/cancelacion-alumno', p).then((r) => r.data);
}

export function enqueueHeatmapDiario(p: { desde: string; hasta: string }) {
  return axios.post<{ jobId: string }>('/reports/heatmap-diario', p).then((r) => r.data);
}

/* tipos */
export interface HeatmapCell {
  date: string;
  value: number;
}
export interface HeatmapDiarioResult {
  url: string;
  registros: number;
  rows: HeatmapCell[];
  userId: number;
}
export async function enqueueHeatmapHorario(payload: {
  desde: string;
  hasta: string;
  centroId?: number;
  tipoId?: number;
}) {
  const { data } = await axios.post('/reports/heatmap-horario', payload);
  return data as { jobId: string };
}

/* tipos */
export type HeatCell = { dow: number; hora: number; pct: number; abs: number };
export type HeatHorarioResult = {
  url: string | null;
  registros: number;
  rows: HeatCell[];
  profesores: { id: number; nombre: string }[]; // ← añadido
  userId: number;
  index: Record<string, number[]>;
};
export interface TotTipoRow {
  tipoId: number;
  tipo: string;
  n: number;
}
export interface TotProfesorResult {
  total: number;
  rows: { tipo: string; n: number }[];
  detalle: Record<string, Record<string, string[]>>; // ← anidado
  url: null;
  registros: number;
  userId: number;
}
export const enqueueTotalesProfesor = () =>
  axios.post('/reports/profesor-totales').then((r) => r.data as { jobId: string });

export const enqueueReservasMetricas = (p: {
  desde: string;
  hasta: string;
  top?: number; // TOP-N alumnos / profesores  (por defecto 10)
}) => axios.post<{ jobId: string }>('/reports/reservas-metricas', p).then((r) => r.data);

/* Enqueue métricas de reuniones */
export const enqueueReunionesMetricas = (p: { desde: string; hasta: string }) =>
  axios.post<{ jobId: string }>('/reports/reuniones-metricas', p).then((r) => r.data);

/* ▸ types de filas ---------------------------------------------------- */
export type VolumenDiarioRow = { fecha: string; n: number; horas: number };
export type EstadoRow = { estado: string; n: number };
export type TopRow = { id: number; nombre: string; n: number };

/* ▸ payload completo que devuelve GET /reports/:id/status ------------- */
export type ReservasMetricasResult = {
  volumenDiario: VolumenDiarioRow[];
  estado: EstadoRow[];
  leadTime: { avg: number; min: number; max: number };
  antiguedad: { abiertas: number; ejecutadas: number };
  topAlumnos: TopRow[];
  topProfesores: TopRow[];
  topCentros: TopRow[];
  url: string; // enlace XLSX
  registros: number;
  userId: number;
};

export async function fetchTotalReservas(payload: any) {
  const { data } = await axios.post<{ n: number | undefined }>('/reports/total-reservas', payload);
  return data?.n ?? 0; // garantiza número
}
/* ════════════ Cancelación – tasa global ════════════ */
export type CancelTasaResult = {
  totOK: number;

  cancelTot: number; // total cancelaciones
  cancelPct: number; // cancelTot / totOK

  cancelAlu: number; // por alumno
  cancelAluPct: number;

  cancelInt: number; // internas (cancelTot - cancelAlu)
  cancelIntPct: number;

  url: string; // XLSX opcional
  registros: number; // siempre 1
  userId: number;
};

export async function enqueueCancelacionTasa(payload: { desde: string; hasta: string }) {
  const { data } = await axios.post<{ jobId: string }>('/reports/cancelacion-tasa', payload);
  return data; // { jobId }
}

export type HeatHorarioResRow = {
  dow: number;
  hora: number;
  pct: number;
  pctGlobal?: number;
  abs: number;
};
export interface HeatHorarioReservasResult {
  url: string;
  registros: number;
  rows: HeatHorarioResRow[];
  userId: number;
}

export const enqueueHeatmapHorarioReservas = (p: { desde: string; hasta: string }) =>
  axios.post<{ jobId: string }>('/reports/heatmap-horario-reservas', p).then((r) => r.data);

export async function enqueueReservasDistribucion(payload: {
  campo: 'curso' | 'proyecto' | 'formato' | 'centro' | 'estado';
  desde: string;
  hasta: string;
}) {
  const { data } = await axios.post('/reports/reservas-distribucion', payload);
  return data as { jobId: string };
}

/* ---------- tipos ---------- */
export interface DistribRow {
  label: string; // «G12 IB», «Apoyo 365», …
  n: number; // nº reservas
}

export interface DistribReservasResult {
  campo: 'curso' | 'proyecto' | 'formato' | 'centro' | 'estado';
  desde: string;
  hasta: string;
  rows: DistribRow[];
  registros: number;
  url: string;
  userId: number;
}
export async function enqueueReservasDiario(payload: { desde: string; hasta: string }) {
  const { data } = await axios.post('/reports/reservas-diario', payload);
  return data as { jobId: string };
}

/* tipos */
export interface ReservasDiaRow {
  fecha: string;
  n: number;
  horas: number;
}

export interface ReservasDiarioResult {
  desde: string;
  hasta: string;
  rows: ReservasDiaRow[];
  registros: number;
  url: string;
  userId: number;
}

/* ────────────── carga por formato (profesor/centro) ────────────── */
export type CargaFormatoRow = {
  profesorId: number;
  profesor: string;
  centroId: number | null;
  centro: string;
  formato: 'Online' | 'Onsite' | 'Home';
  reservas: number;
  horas: number;
};

export type CargaFormatoResult = {
  url: string;
  registros: number;
  rows: CargaFormatoRow[];
  userId: number;
};

export const enqueueCargaFormatoProfesorCentro = (p: {
  desde: string;
  hasta: string;
  profesorId?: number;
  centroId?: number;
}) => axios.post<{ jobId: string }>('/reports/carga-formato-prof-centro', p).then((r) => r.data);

/* ────────────── utilización capacidad centro/franja ────────────── */
export type UtilizacionCapacidadRow = {
  centroId: number;
  centro: string;
  hora: number;
  horasDisponibles: number;
  horasReservadas: number;
  pct: number;
};

export type UtilizacionCapacidadResult = {
  url: string;
  registros: number;
  rows: UtilizacionCapacidadRow[];
  userId: number;
};

export const enqueueUtilizacionCapacidadCentro = (p: {
  desde: string;
  hasta: string;
  centroId?: number;
}) => axios.post<{ jobId: string }>('/reports/utilizacion-capacidad-centro', p).then((r) => r.data);

/* ────────────── ocupación de aulas por centro (calendario) ────────────── */
export type OcupacionCentroDiaRow = {
  fecha: string;
  centroId: number;
  centro: string;
  totalAulas: number;
  maxPct: number;
  tramosSinDisponibles: number;
  tramosSobre90: number;
  tramosSobre70: number;
  estado: 'red' | 'orange' | 'yellow' | 'green';
};

export type OcupacionCentroHoraRow = {
  fecha: string;
  centroId: number;
  centro: string;
  hora: number;
  totalAulas: number;
  aulasOcupadas: number;
  aulasBloqueadas: number;
  aulasDisponibles: number;
  pct: number;
  aulasOcupadasDetalle: { aulaId: number; aula: string }[];
  aulasDisponiblesDetalle: { aulaId: number; aula: string }[];
};

export type OcupacionCentroResult = {
  url: string;
  registros: number;
  rowsDaily: OcupacionCentroDiaRow[];
  rowsHourly: OcupacionCentroHoraRow[];
  summary: {
    centroId: number;
    centro: string;
    totalAulas: number;
    diasEnRojo: number;
    diasEnNaranja: number;
    diasEnAmarillo: number;
    diasEnVerde: number;
  };
  userId: number;
};

export const enqueueOcupacionCentro = (p: { desde: string; hasta: string; centroId: number }) =>
  axios.post<{ jobId: string }>('/reports/ocupacion-centro', p).then((r) => r.data);

/* ────────────── tasa incidencias por profesor/centro ────────────── */
export type IncidenciasTasaRow = {
  id: number | null;
  nombre: string;
  incidencias: number;
  total: number;
  tasa: number;
};

export type IncidenciasTasaResult = {
  url: string;
  registros: number;
  porProfesor: IncidenciasTasaRow[];
  porCentro: IncidenciasTasaRow[];
  userId: number;
};

export const enqueueIncidenciasTasa = (p: {
  desde: string;
  hasta: string;
  profesorId?: number;
  centroId?: number;
}) => axios.post<{ jobId: string }>('/reports/incidencias-tasa', p).then((r) => r.data);
export type FirmasDiaTotales = {
  firmadas: number;
  pendProfe: number;
  pendAlu: number;
  noFirmada: number;
};

export type FirmasDiaDetalle = {
  id: number;
  hora: string; // "10:00-12:00"
  estado: 'Firmada' | 'Pendiente profe' | 'Pendiente alumnos' | 'No firmada';
  profesor: number;
  cliente: number;
};

export type FirmasDiaResult = {
  fecha: string; // YYYY-MM-DD
  totales: FirmasDiaTotales;
  detalles: FirmasDiaDetalle[];
  url: string; // link al XLSX
};
export async function enqueueFirmasDia(payload: { fecha: string }): Promise<{ jobId: string }> {
  const { data } = await axios.post<{ jobId: string }>('/reports/firmas-dia', payload);
  return data;
}
export type FirmaDet = {
  id: number;
  nombre: string;
  rol: 'profesor' | 'alumno';
  metodo: 'DIGITAL' | 'FORM';
  motivo?: string | null;
};

export async function fetchFirmasReserva(reservaId: number) {
  const { data } = await axios.get<FirmaDet[]>(`/reports/reserva/${reservaId}/firmas`);
  return data; // ← array
}

/* ---------- BLOB PNG ----------------------------------------- */
export async function fetchFirmaBlob(firmaId: number) {
  const { data } = await axios.get<ArrayBuffer>(
    `/reports/firma/${firmaId}`,
    { responseType: 'arraybuffer' }, // ⬅️ blob binario
  );
  return new Blob([data], { type: 'image/png' });
}
/* enqueue ----------------------------------------- */
export const enqueueAusenciasTotales = (p: { desde: string; hasta: string }) =>
  axios.post<{ jobId: string }>('/reports/ausencias-totales', p).then((r) => r.data);

export const enqueueAusenciasProfesor = (p: { desde: string; hasta: string }) =>
  axios.post<{ jobId: string }>('/reports/ausencias-profesor', p).then((r) => r.data);

/* tipos resultado --------------------------------- */
export type AusTotRow = { tipo: string; n: number };
export type AusenciasTotalesResult = {
  url: string;
  registros: number;
  rows: AusTotRow[];
  userId: number;
};
export interface AusDetRow {
  id: number; // ← id de la ausencia
  adjN: number; // ← nº de adjuntos
  fecha: string;
  tipo: 'justificada' | 'injustificada';
  motivo: string | null;
  parcial: boolean;
}
export type AusProfRow = {
  profesorId: number;
  profesor: string;
  justN: number;
  injustN: number;
  totalN: number;
  ultima: string;
  detalle: AusDetRow[]; // 👈 NUEVO
};
export type AusenciasProfesorResult = {
  url: string | null;
  registros: number;
  rows: AusProfRow[];
  userId: number;
};

export type HpProfRow = {
  profesorId: number;
  profesor: string;
  reservas: number;
  horas: number;
  alumnos: number;
};

export type HpProfesoresResult = {
  url: null;
  registros: number;
  rows: HpProfRow[];
  userId: number;
};

export type HpReservaRow = {
  reservaId: number;
  fecha: string; // YYYY-MM-DD
  horas: number; // nº horas de la sesión
  asignatura: string;
  totalAlumnos: number;
  alumnosFirma: number; // firmados en APP
  alumnosFormulario: number; // firmados por formulario
  alumnosSinFirma: number; // pendientes
};

export type HpReservasResult = {
  url: null;
  registros: number;
  rows: HpReservaRow[];
  userId: number;
};

export type HpFirmaDetRow = {
  alumnoId: number;
  alumno: string;
  firma: boolean; // app
  formulario: boolean; // form
  estado: 'firma' | 'formulario' | 'pendiente';
};

export type HpReservaFirmasResult = {
  url: null;
  registros: number;
  rows: HpFirmaDetRow[];
  userId: number;
};

/* enqueue */
export const enqueueVacacionesProfesor = (p: { year: number }) =>
  axios.post<{ jobId: string }>('/reports/vacaciones-profesor', p).then((r) => r.data);

/* tipos – filas y payload */
export interface VacDetRow {
  fecha: string; // dd-mm-yyyy en worker ya está formateado
  creado: string; // dd-mm-yyyy
}
export interface VacProfRow {
  profesorId: number;
  profesor: string;
  diasN: number;
  ultima: string;
  detalle: VacDetRow[];
}
export interface VacacionesProfesorResult {
  url: string;
  registros: number;
  rows: VacProfRow[];
  userId: number;
}

// --- tipos -----------------------------------------------------
export type TrainingRangePayload = {
  desde: string; // YYYY-MM-DD
  hasta: string; // YYYY-MM-DD
  centroId?: number;
  tipoId?: number; // tipo de asignatura
};

export type TrainingTotalResult = {
  formaciones: number;
  horas: number;
  registros: number; // =1 para mantener contrato
  url: null;
  userId: number;
};
export type TrainingPorTipoRow = {
  tipoId: number;
  tipo: string;
  formaciones: number;
  horas: number;
};
export type TrainingPorTipoResult = {
  registros: number;
  rows: TrainingPorTipoRow[];
  url: null;
  userId: number;
};
export type TrainingImpartidasRow = {
  profesorId: number;
  profesor: string;
  formaciones: number;
  horas: number;
};
export type TrainingImpartidasResult = {
  registros: number;
  rows: TrainingImpartidasRow[];
  url: null;
  userId: number;
};
export type TrainingRecibidasResult = TrainingImpartidasResult; // misma shape

export interface DistribAluRow {
  id: number;
  nombre: string;
  email: string;
  telefono: string;
}
export interface DistribAlumnosResult {
  campo: 'curso' | 'proyecto' | 'formato' | 'centro' | 'estado';
  valor: string;
  desde: string;
  hasta: string;
  rows: DistribAluRow[];
  registros: number;
  url: string; // XLSX
  userId: number;
}

export function enqueueDistribucionAlumnos(payload: {
  campo: 'curso' | 'proyecto' | 'formato' | 'centro' | 'estado';
  valor: string;
  desde: string;
  hasta: string;
}) {
  return axios
    .post<{ jobId: string }>('/reports/reservas-distribucion/alumnos', payload)
    .then((r) => r.data);
}

// --- enqueue helpers -------------------------------------------
export const enqueueTrainingTotal = (p: TrainingRangePayload) =>
  axios.post<{ jobId: string }>('/reports/training-total', p).then((r) => r.data);
export const enqueueTrainingPorTipo = (p: TrainingRangePayload) =>
  axios.post<{ jobId: string }>('/reports/training-por-tipo', p).then((r) => r.data);
export const enqueueTrainingImpartidas = (p: TrainingRangePayload) =>
  axios.post<{ jobId: string }>('/reports/training-impartidas', p).then((r) => r.data);
export const enqueueTrainingRecibidas = (p: TrainingRangePayload) =>
  axios.post<{ jobId: string }>('/reports/training-recibidas', p).then((r) => r.data);

export interface AgendaDetRow {
  fecha: string;
  kind: 'vac' | 'aus';
  tramo?: 'completo' | 'manana' | 'tarde';
  tipoAbs?: 'justificada' | 'injustificada';
  motivo?: string | null;
  horaIni?: string | null;
  horaFin?: string | null;
}

export interface AgendaProfRow {
  profesorId: number;
  profesor: string;
  detalle: AgendaDetRow[];
}

// --- payload completo --------------------------------------------------------
export interface AgendaProfesorResult {
  url: null;
  registros: number;
  rows: AgendaProfRow[];
  userId: number;
}

/* POST /reports/agenda-profesor */
export const enqueueAgendaProfesor = (p: { desde?: string; hasta?: string }) =>
  axios.post<{ jobId: string }>('/reports/agenda-profesor', p).then((r) => r.data);

export type EstadoFirma =
  | 'Firmada'
  | 'No firmado por el profesor'
  | 'No firmado por alumnos'
  | 'Firmado por formulario';

export type FirmasPeriodoDet = {
  id: number;
  fecha: string;
  hora: string;
  estado: EstadoFirma;
  proyecto: string;
  asignatura: string;
  profesorId: number;
  profesor: string;
  clienteId: number;
  cliente: string;
};

export type FirmasProfesorRow = {
  profesorId: number;
  profesor: string;
  firmadas: number;
  noProf: number;
  noAlu: number;
  form: number;
  total: number;
};

export type FirmasPeriodoResult = {
  desde: string;
  hasta: string;
  totales: { firmadas: number; noProf: number; noAlu: number; form: number };
  detalles: FirmasPeriodoDet[];
  profesores: FirmasProfesorRow[];
  url: string; // xlsx
  registros: number;
  userId: number;
};

export const enqueueFirmasPeriodo = (p: { desde: string; hasta?: string }) =>
  axios.post<{ jobId: string }>('/reports/firmas', p).then((r) => r.data);

export const enqueueHighPerfProfesores = (p: { desde: string; hasta: string }) =>
  axios.post<{ jobId: string }>('/reports/highperf-profesores', p).then((r) => r.data);

export const enqueueHighPerfReservas = (p: { profesorId: number; desde: string; hasta: string }) =>
  axios.post<{ jobId: string }>('/reports/highperf-reservas', p).then((r) => r.data);

export const enqueueHighPerfReservaFirmas = (p: { reservaId: number }) =>
  axios.post<{ jobId: string }>('/reports/highperf-reserva-firmas', p).then((r) => r.data);

export function enqueueAlumnosPorAlumno(p: { alumnoId: number; desde: string; hasta: string }) {
  return axios
    .post<{ jobId: string }>('/reports/alumnos-proyecto-alumno', p) // endpoint nuevo
    .then((r) => r.data);
}

export type AlumnoProyectoMensualRow = {
  alumnoId: number;
  alumno: string;
  proyectoId: number | null;
  proyecto: string;
  monthKey: string; // "2026-01"
  monthLabel: string; // "Ene 2026"
  horas: number;
};

export type AlumnoProyectoMensualResult = {
  url: string;
  registros: number;
  rows: AlumnoProyectoMensualRow[];
  userId: number;
};
